// S19 — the impersonation banner (spec 15 §3.7 step 4, §4 S19; D-492, D-620).
// Under a session a platform staff member opened through a grant, every
// tenant area shows who from Assessly is using the account, the ticket, the
// mode and the time left, with End. The facts come from the session's own
// profile (module 16 copied them onto the session row at redemption); a
// tenant request cannot read schema `platform`, so nothing here asks it.
//
// End ends the grant and every session minted under it (`POST
// /v1/auth/impersonation/end`), then leaves for the "session ended" page.
// It is allowed under a read-only grant, like the banner's own sign-out.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { profileReadEpoch, readProfileOnce, resetProfileRead } from "../lib/profile-locale";
import { resetRefNames } from "../lib/ref-names";
import { resetRoleFeed } from "../lib/role-codes";
import { Button } from "../components/button";

type Banner = NonNullable<
  NonNullable<Awaited<ReturnType<typeof readProfileOnce>>>["impersonation"]
>;

/** Whole minutes left before `expiresAt`, never below zero. Pure. */
export function bannerMinutesLeft(expiresAt: string, now: Date): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000));
}

function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return now;
}

export function ImpersonationBanner() {
  const profile = useQuery({
    queryKey: ["auth-profile", "impersonation", profileReadEpoch()],
    queryFn: () => readProfileOnce(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const banner = profile.data?.impersonation ?? null;
  if (banner === null) return null;
  return <BannerBar banner={banner} />;
}

function BannerBar({ banner }: { banner: Banner }) {
  const { t } = useTranslation();
  const now = useNow();
  const queryClient = useQueryClient();
  const end = useMutation({
    networkMode: "always",
    mutationFn: async () => {
      const result = await client.POST("/v1/auth/impersonation/end");
      if (!result.response.ok) throw new Error(`end failed: ${String(result.response.status)}`);
    },
    onSuccess: () => {
      // A session change forgets the names and roles read (D-441).
      resetProfileRead();
      resetRefNames(queryClient);
      resetRoleFeed(queryClient);
      // A full navigation: the session is gone, and no cached tenant read
      // may survive into the page that says so.
      window.location.assign("/impersonate?ended=1");
    },
  });
  const who =
    banner.staff_display_name !== null
      ? t("admin.banner.viewing", { name: banner.staff_display_name })
      : t("admin.banner.viewingUnnamed");
  return (
    <div
      className="imp-banner"
      role="status"
      data-testid="impersonation-banner"
      data-mode={banner.mode}
    >
      <span className="imp-banner-who">{who}</span>
      {banner.ticket_ref !== null ? (
        <span className="imp-banner-meta" data-testid="impersonation-banner-ticket">
          {t("admin.banner.ticket", { ticket: banner.ticket_ref })}
        </span>
      ) : null}
      <span className="imp-banner-meta" data-testid="impersonation-banner-mode">
        {t(`admin.banner.mode.${banner.mode}`)}
      </span>
      <span className="imp-banner-meta" data-testid="impersonation-banner-left">
        {t("admin.banner.left", { minutes: bannerMinutesLeft(banner.expires_at, now) })}
      </span>
      {end.isError ? (
        <span className="imp-banner-meta" role="alert">
          {t("admin.banner.endFailed")}
        </span>
      ) : null}
      <Button
        size="sm"
        busy={end.isPending}
        busyLabel={t("admin.banner.ending")}
        data-testid="impersonation-banner-end"
        onClick={() => {
          end.mutate();
        }}
      >
        {t("admin.banner.end")}
      </Button>
    </div>
  );
}
